/**
 * Who you are going with, and where.
 *
 * A short list of names with their health, drawn beside the friends list
 * because that is where somebody looks for the people they are playing with.
 * The mark is the point of it: one person says where the party is headed and
 * it appears on everybody's map.
 */

import type { PartyWire } from '@inverse/protocol';

export function Party({
  party,
  me,
  waypoint,
  onLeave,
  onShare,
  onClearPoint,
  onGoTo,
}: {
  party: PartyWire;
  me: string;
  /** Where this person is headed on their own, so it can be shared. */
  waypoint: { tx: number; tz: number; layer: number } | null;
  onLeave: () => void;
  onShare: (at: { tx: number; tz: number; layer: number }) => void;
  onClearPoint: () => void;
  /** Takes this person's own mark to where the party is headed. */
  onGoTo: (at: { tx: number; tz: number; layer: number }) => void;
}): JSX.Element {
  if (party.members.length === 0) {
    return (
      <div className="party">
        <p className="hint">
          You are on your own. Right click somebody and ask them into a party, and where any of you
          are and where you are headed shows on the map.
        </p>
      </div>
    );
  }

  return (
    <div className="party">
      <div className="party-list">
        {party.members.map((one) => {
          const part = one.maxHealth > 0 ? Math.max(0, one.health / one.maxHealth) : 0;
          return (
            <div key={one.name} className={one.name === me ? 'party-one me' : 'party-one'}>
              <span className="party-name">
                {one.name === party.leader ? '★ ' : ''}
                {one.name}
              </span>
              <span className="party-health" title={`${one.health} of ${one.maxHealth}`}>
                <span style={{ width: `${Math.round(part * 100)}%` }} />
              </span>
              <small>{one.near ? `${one.tx}, ${one.tz}` : 'elsewhere'}</small>
            </div>
          );
        })}
      </div>

      {party.point ? (
        <div className="party-point">
          <span>
            Headed for {party.point.tx}, {party.point.tz}, by {party.point.by}
          </span>
          <div className="row">
            <button
              type="button"
              onClick={() =>
                onGoTo({
                  tx: party.point?.tx ?? 0,
                  tz: party.point?.tz ?? 0,
                  layer: party.point?.layer ?? 0,
                })
              }
            >
              Mark it for me
            </button>
            <button type="button" onClick={onClearPoint}>
              Take it away
            </button>
          </div>
        </div>
      ) : (
        <p className="hint">
          Nowhere marked yet. Put a waypoint on the map and share it, and everybody sees it.
        </p>
      )}

      <div className="row">
        <button type="button" disabled={!waypoint} onClick={() => waypoint && onShare(waypoint)}>
          Share my waypoint
        </button>
        <button type="button" className="danger" onClick={onLeave}>
          Leave the party
        </button>
      </div>
    </div>
  );
}
